import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Star, Calendar, Trophy, Plus, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { TeenOfTheDaySubmission } from '@/types/teen-of-the-day';
import TeenSubmissionDialog from './TeenSubmissionDialog';
import { Link } from 'react-router-dom';

const TeenOfTheDay = () => {
  const { user } = useAuth();
  const [currentTeen, setCurrentTeen] = useState<TeenOfTheDaySubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);

  useEffect(() => {
    fetchCurrentTeen();
  }, []);

  const fetchCurrentTeen = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('teen_of_the_day_submissions')
        .select('*')
        .eq('status', 'approved')
        .order('featured_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching teen of the day:', error);
        return;
      }

      if (data) {
        // Fetch user profile separately
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('full_name, avatar_url')
          .eq('id', data.user_id)
          .maybeSingle();

        setCurrentTeen({
          ...data,
          status: data.status as 'pending' | 'approved' | 'rejected',
          user_name: profileData?.full_name || 'Anonymous Teen',
          user_avatar: profileData?.avatar_url
        });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  if (loading) {
    return (
      <motion.div 
        className="min-h-screen bg-background"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="h-8 w-64 bg-gray-200 rounded-lg mx-auto mb-4 animate-pulse"></div>
            <div className="h-4 w-96 bg-gray-200 rounded-lg mx-auto animate-pulse"></div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (!currentTeen) {
    return (
      <motion.div 
        className="min-h-screen bg-background"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Hero Section */}
        <motion.div 
          className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent py-16"
          variants={sectionVariants}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <motion.h1 
                className="text-4xl md:text-5xl font-bold mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Teen <span className="text-primary">Inspiration</span>
              </motion.h1>
              <motion.p 
                className="text-xl text-muted-foreground mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Be the first to be featured! Share your story and inspire other teens through your journey, 
                achievements, and dreams.
              </motion.p>
            </div>
          </div>
        </motion.div>

        {/* Call to Action Section */}
        <motion.div 
          className="container mx-auto px-4 py-16"
          variants={sectionVariants}
        >
          <div className="text-center py-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-semibold mb-4">No Featured Teen Yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-8">
                Featured teens will appear here once they are added to the platform.
              </p>
              
              {!user && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-muted/50 rounded-2xl p-8 mb-8 max-w-2xl mx-auto"
                >
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Sparkles className="h-6 w-6 text-primary" />
                    <h3 className="text-2xl font-bold">Want to be Featured?</h3>
                  </div>
                  <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                    Sign up for Teenvisor and explore your skills! Discover your potential, build your profile, 
                    and then request to be featured.
                  </p>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button 
                      size="lg"
                      className="bg-gradient-to-r from-primary to-primary/80"
                      asChild
                    >
                      <Link to="/auth">
                        <Sparkles className="h-5 w-5 mr-2" />
                        Join the Community
                      </Link>
                    </Button>
                  </motion.div>
                </motion.div>
              )}
              
              {user && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    onClick={() => setShowSubmissionDialog(true)}
                    size="lg"
                    className="bg-gradient-to-r from-primary to-primary/80"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Request to be Featured
                  </Button>
                </motion.div>
              )}
            </motion.div>
          </div>
        </motion.div>
        
        <TeenSubmissionDialog 
          open={showSubmissionDialog}
          onOpenChange={setShowSubmissionDialog}
          onSuccess={fetchCurrentTeen}
        />
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="min-h-screen bg-background"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Hero Section */}
      <motion.div 
        className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent py-16"
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Featured <span className="text-primary">Teen</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Discover inspiring stories of young achievers who are making a difference. 
              Learn from their journeys, challenges, and victories.
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Featured Teen Section */}
      <motion.div 
        className="container mx-auto px-4 py-16"
        variants={sectionVariants}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <Card className="overflow-hidden border-0 bg-card shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="text-center pb-6">
              <div className="flex flex-col items-center gap-4">
                <Avatar className="h-20 w-20 ring-4 ring-primary/20">
                  <AvatarImage src={currentTeen.user_avatar} alt={currentTeen.user_name} />
                  <AvatarFallback className="bg-gradient-to-r from-primary to-primary/80 text-white text-xl font-bold">
                    {currentTeen.user_name?.split(' ').map(n => n[0]).join('') || 'T'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-2xl md:text-3xl font-bold mb-2">
                    {currentTeen.user_name}
                  </CardTitle>
                  <CardDescription className="text-lg font-medium text-primary">
                    {currentTeen.title}
                  </CardDescription>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Featured on {new Date(currentTeen.featured_date || currentTeen.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {currentTeen.image_url && (
                <div className="relative h-64 md:h-80 rounded-xl overflow-hidden">
                  <img
                    src={currentTeen.image_url}
                    alt={currentTeen.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {currentTeen.story}
                </p>
              </div>

              {currentTeen.achievements && (
                <div className="bg-muted/50 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Trophy className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">Achievements</h3>
                  </div>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {currentTeen.achievements}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center pt-4">
                <Badge variant="secondary" className="px-4 py-2">
                  🌟 Teen Inspiration
                </Badge>
                {user && (
                  <Button 
                    onClick={() => setShowSubmissionDialog(true)}
                    variant="outline"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Request to be Featured
                  </Button>
                )}
                {!user && (
                  <Button variant="outline" asChild>
                    <Link to="/auth">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Join Teenvisor
                    </Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="bg-muted/50 py-16"
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4 text-center">
          <motion.h2 
            className="text-3xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Ready to Share Your Story?
          </motion.h2>
          <motion.p 
            className="text-muted-foreground mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Join our community of inspiring teens and share your journey. 
            Get featured and inspire others with your achievements and dreams.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {user ? (
              <Button 
                onClick={() => setShowSubmissionDialog(true)}
                size="lg" 
                className="bg-gradient-to-r from-primary to-primary/80"
              >
                <Plus className="h-5 w-5 mr-2" />
                Request to be Featured
              </Button>
            ) : (
              <Button size="lg" className="bg-gradient-to-r from-primary to-primary/80" asChild>
                <Link to="/auth">
                  Join the Community
                </Link>
              </Button>
            )}
          </motion.div>
        </div>
      </motion.div>

      <TeenSubmissionDialog 
        open={showSubmissionDialog}
        onOpenChange={setShowSubmissionDialog}
        onSuccess={fetchCurrentTeen}
      />
    </motion.div>
  );
};

export default TeenOfTheDay;
